import { useState } from "react";
import { Copy, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, formatPrice } from "@/lib/api";

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }) : "");

export default function OrderAdjustments({ order, onDone }) {
  const [busy, setBusy] = useState(false);
  const sid = order.session_id;
  const sup = order.supplement;

  const sendLink = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/orders/${sid}/balance-link`, { origin_url: window.location.origin });
      data.emailed ? toast.success(data.message) : toast.warning(data.message);
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Envoi impossible");
    } finally {
      setBusy(false);
    }
  };

  const copy = () => navigator.clipboard.writeText(sup.url).then(() => toast.success("Lien copié"));

  const renew = async (method) => {
    if (!window.confirm(method === "cheque" ? "Passer cette commande en paiement par chèque et envoyer les instructions au client ?" : "Envoyer au client un nouveau lien de paiement PayPal ?")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/orders/${sid}/renew-payment`, { method, origin_url: window.location.origin });
      data.emailed ? toast.success(data.message) : toast.warning(data.message);
      if (data.url && !data.emailed) navigator.clipboard.writeText(data.url).then(() => toast.info("Lien PayPal copié dans le presse-papiers")).catch(() => {});
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Relance impossible");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 space-y-2 text-xs">
      {order.payment_status === "pending" && (
        <div data-testid={`order-renew-${sid}`} className="flex flex-wrap items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 text-bark">
          <span className="font-semibold">Paiement non finalisé — relancer :</span>
          <button data-testid={`order-renew-paypal-${sid}`} disabled={busy} onClick={() => renew("paypal")} className="flex items-center gap-1 rounded-full bg-amber-deep px-3 py-1 font-medium text-white hover:bg-honey disabled:opacity-60"><Send size={11} /> Nouveau lien PayPal</button>
          <button data-testid={`order-renew-cheque-${sid}`} disabled={busy} onClick={() => renew("cheque")} className="flex items-center gap-1 rounded-full border border-amber-800/25 bg-white px-3 py-1 font-medium text-bark hover:border-amber-deep">Passer en chèque</button>
        </div>
      )}
      {order.renewed_to && <p className="text-stone-mute">Remplacée par la commande n° {order.renewed_to.slice(-8).toUpperCase()} (nouveau lien de paiement)</p>}
      {order.payment_status === "cancelled" && (
        <p data-testid={`order-cancel-info-${sid}`} className="rounded-lg bg-stone-100 px-3 py-2 text-bark-soft">
          Annulée le {fmtDate(order.cancelled_at)}{order.cancel_reason && ` — « ${order.cancel_reason} »`}
          {order.refunded_cents > 0 && <span className="font-semibold text-sage"> · {formatPrice(order.refunded_cents)} remboursés via PayPal</span>}
        </p>
      )}
      {order.payment_status === "paid" && order.balance_due_cents > 0 && (
        <div data-testid={`order-balance-due-${sid}`} className="flex flex-wrap items-center gap-2 rounded-lg bg-amber-100 px-3 py-2 text-amber-900">
          <span className="font-semibold">Complément à payer : {formatPrice(order.balance_due_cents)}</span>
          {sup?.status === "pending" && <span>· lien envoyé le {fmtDate(sup.sent_at)}</span>}
          <button data-testid={`order-send-balance-link-${sid}`} disabled={busy} onClick={sendLink} className="flex items-center gap-1 rounded-full bg-amber-deep px-3 py-1 font-medium text-white hover:bg-honey disabled:opacity-60">
            <Send size={11} /> {sup?.status === "pending" ? "Renvoyer le lien" : "Envoyer le lien de paiement"}
          </button>
          {sup?.status === "pending" && sup.url && (
            <button data-testid={`order-copy-balance-link-${sid}`} onClick={copy} className="flex items-center gap-1 rounded-full border border-amber-800/25 bg-white px-3 py-1 font-medium text-bark"><Copy size={11} /> Copier le lien</button>
          )}
        </div>
      )}
      {order.refund_due_cents > 0 && order.payment_status === "paid" && (
        <p data-testid={`order-refund-due-${sid}`} className="rounded-lg bg-sage-light px-3 py-2 font-semibold text-sage">À rembourser au client : {formatPrice(order.refund_due_cents)}</p>
      )}
      {(order.supplements_paid || []).map((s) => (
        <p key={s.capture_id} className="rounded-lg bg-sage-light px-3 py-2 text-sage">Complément de {formatPrice(s.amount_cents)} payé le {fmtDate(s.at)}</p>
      ))}
      {order.edits?.length > 0 && (
        <details data-testid={`order-edits-${sid}`} className="text-stone-mute">
          <summary className="cursor-pointer">Modifiée {order.edits.length} fois</summary>
          <ul className="mt-1 space-y-1 pl-3">
            {order.edits.map((e) => (
              <li key={e.at}>{fmtDate(e.at)} : {formatPrice(e.old_total_cents)} → {formatPrice(e.new_total_cents)} ({e.changes.join(", ")})</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
